import React, { useState, useEffect } from 'react';
import { Users, Plus, Trash2, Search, Filter, Loader2, CheckCircle, XCircle, Shield, Clock, Building2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { normalizeAppError } from '../../utils/apiErrors';
import { API } from '../../config/apiBase';


export default function EmpresaColaboradoresPage() {
    const [colaboradores, setColaboradores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ name: '', email: '', password: '', role: 'colaborador' });
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [filterRole, setFilterRole] = useState('all');
    const [unidades, setUnidades] = useState([]);

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const fetchColaboradores = async () => {
        setLoading(true);
        try {
            const r = await fetch(`${API}/client/users`, { headers });
            const data = await r.json();
            setColaboradores(Array.isArray(data) ? data : []);
        } catch { setColaboradores([]); }
        setLoading(false);
    };

    const fetchUnidades = async () => {
        try {
            const r = await fetch(`${API}/ponto/unidades`, { headers });
            if (r.ok) { const data = await r.json(); setUnidades(Array.isArray(data) ? data : []); }
        } catch {}
    };

    useEffect(() => { fetchColaboradores(); fetchUnidades(); }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const r = await fetch(`${API}/client/users`, { method: 'POST', headers, body: JSON.stringify(form) });
            const data = await r.json();
            if (!r.ok) throw new Error(data.detail || 'Erro');
            toast.success('Colaborador cadastrado com sucesso!');
            setShowForm(false);
            setForm({ name: '', email: '', password: '', role: 'colaborador' });
            fetchColaboradores();
        } catch (err) { toast.error(normalizeAppError(err).message); }
        setSaving(false);
    };

    const handleDelete = async (uid, name) => {
        if (!window.confirm(`Remover ${name}?`)) return;
        try {
            const r = await fetch(`${API}/client/users/${uid}`, { method: 'DELETE', headers });
            if (!r.ok) throw new Error('Erro ao remover');
            toast.success('Colaborador removido');
            fetchColaboradores();
        } catch (err) { toast.error(normalizeAppError(err).message); }
    };

    const handleAssignUnidade = async (userId, unidadeId) => {
        try {
            const r = await fetch(`${API}/client/users/${userId}/unidade`, {
                method: 'PATCH', headers, body: JSON.stringify({ unidade_id: unidadeId || null })
            });
            if (!r.ok) { const d = await r.json(); throw new Error(d.detail || 'Erro'); }
            toast.success('Unidade atribuída!');
            fetchColaboradores();
        } catch (err) { toast.error(normalizeAppError(err).message); }
    };

    const filtered = colaboradores.filter(c => {
        const matchSearch = !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.email?.toLowerCase().includes(search.toLowerCase());
        const matchRole = filterRole === 'all' || c.role === filterRole || (filterRole === 'gestor' && (c.role === 'gestor' || c.role === 'empresa_gestor'));
        return matchSearch && matchRole;
    });

    const gestoresCount = colaboradores.filter(c => c.role === 'empresa_gestor' || c.role === 'gestor').length;
    const colabCount = colaboradores.filter(c => c.role === 'colaborador' || c.role === 'funcionario').length;

    return (
        <div className="space-y-5" data-testid="empresa-colaboradores">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl p-3.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <p className="text-xl font-bold" style={{ color: 'var(--text)' }}>{colaboradores.length}</p>
                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Total</p>
                </div>
                <div className="rounded-xl p-3.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <p className="text-xl font-bold" style={{ color: '#3B82F6' }}>{gestoresCount}</p>
                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Gestores</p>
                </div>
                <div className="rounded-xl p-3.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <p className="text-xl font-bold" style={{ color: 'var(--sv-gold-primary)' }}>{colabCount}</p>
                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Colaboradores</p>
                </div>
            </div>

            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1 relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted)' }} />
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nome ou email..."
                        className="w-full pl-9 pr-3 h-10 rounded-lg text-sm" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="search-input" />
                </div>
                <div className="flex gap-2">
                    <select value={filterRole} onChange={e => setFilterRole(e.target.value)}
                        className="flex-1 sm:flex-none min-w-[110px] px-3 h-10 rounded-lg text-sm appearance-none" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="filter-role">
                        <option value="all">Todos</option>
                        <option value="colaborador">Colaboradores</option>
                        <option value="gestor">Gestores</option>
                    </select>
                    <Button onClick={() => setShowForm(!showForm)} className="btn-gold text-sm whitespace-nowrap h-10 px-4" data-testid="add-colaborador-btn">
                        <Plus size={14} className="mr-1" /> Cadastrar
                    </Button>
                </div>
            </div>

            {/* Form */}
            {showForm && (
                <form onSubmit={handleCreate} className="rounded-xl p-4 sm:p-5 space-y-3" style={{ background: 'var(--surface)', border: '1px solid rgba(var(--sv-accent-rgb), 0.3)' }} data-testid="colaborador-form">
                    <h4 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Novo Colaborador</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input placeholder="Nome completo" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required
                            className="px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="input-name" />
                        <input placeholder="Email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required
                            className="px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="input-email" />
                        <input placeholder="Senha (min 6 chars)" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required minLength={6}
                            className="px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="input-password" />
                        <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}
                            className="px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="input-role">
                            <option value="colaborador">Colaborador</option>
                            <option value="empresa_gestor">Gestor</option>
                        </select>
                    </div>
                    <div className="flex gap-2">
                        <Button type="submit" disabled={saving} className="btn-gold text-sm" data-testid="submit-colaborador">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : 'Salvar'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="text-sm">Cancelar</Button>
                    </div>
                </form>
            )}

            {/* List */}
            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-14 rounded-xl" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(var(--sv-accent-rgb), 0.08)' }}>
                        <Users size={24} style={{ color: 'var(--muted)', opacity: 0.5 }} />
                    </div>
                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                        {search || filterRole !== 'all' ? 'Nenhum resultado encontrado' : 'Nenhum colaborador cadastrado'}
                    </p>
                    <p className="text-xs mt-1 mb-4 max-w-xs mx-auto" style={{ color: 'var(--muted)' }}>
                        {!search && filterRole === 'all' ? 'Cadastre colaboradores para gerenciar sua equipe, controlar ponto e acompanhar desempenho.' : 'Tente ajustar os filtros de busca.'}
                    </p>
                    {!search && filterRole === 'all' && (
                        <Button onClick={() => setShowForm(true)} className="btn-gold text-sm" data-testid="empty-add-colaborador-btn">
                            <Plus size={14} className="mr-1" /> Cadastrar Primeiro Colaborador
                        </Button>
                    )}
                </div>
            ) : (
                <div className="space-y-2">
                    {filtered.map((c) => {
                        const isGestor = c.role === 'empresa_gestor' || c.role === 'gestor';
                        return (
                            <div key={c.user_id} className="flex items-center gap-3 p-3.5 rounded-xl transition-all hover:scale-[1.005]"
                                style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`colab-${c.user_id}`}>
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold shrink-0"
                                    style={{ background: isGestor ? 'rgba(59,130,246,0.12)' : 'rgba(45,212,191,0.12)', color: isGestor ? '#3B82F6' : '#2DD4BF' }}>
                                    {c.name?.charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{c.name}</p>
                                        <span className="text-[9px] px-1.5 py-0.5 rounded-full shrink-0"
                                            style={{ background: isGestor ? 'rgba(59,130,246,0.12)' : 'rgba(45,212,191,0.10)', color: isGestor ? '#3B82F6' : '#2DD4BF' }}>
                                            {isGestor ? 'Gestor' : 'Colaborador'}
                                        </span>
                                        <span className="text-[9px] px-1.5 py-0.5 rounded-full shrink-0"
                                            style={{ background: c.is_approved ? 'rgba(16,185,129,0.1)' : 'rgba(107,114,128,0.1)', color: c.is_approved ? '#10B981' : '#6B7280' }}>
                                            {c.is_approved ? 'Ativo' : 'Inativo'}
                                        </span>
                                    </div>
                                    <p className="text-xs truncate" style={{ color: 'var(--muted)' }}>{c.email}</p>
                                    {unidades.length > 0 && (
                                        <div className="flex items-center gap-1.5 mt-1">
                                            <Building2 size={10} style={{ color: 'var(--muted)', flexShrink: 0 }} />
                                            <select value={c.unidade_id || ''} onChange={e => handleAssignUnidade(c.user_id, e.target.value)}
                                                className="text-[10px] py-0.5 px-1.5 rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', maxWidth: 160 }}
                                                data-testid={`unidade-select-${c.user_id}`}>
                                                <option value="">Sem unidade</option>
                                                {unidades.map(u => <option key={u.unidade_id} value={u.unidade_id}>{u.name}</option>)}
                                            </select>
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button onClick={() => handleDelete(c.user_id, c.name)} className="p-2 rounded-lg transition-colors hover:bg-red-500/10" data-testid={`delete-${c.user_id}`}>
                                        <Trash2 size={14} style={{ color: '#EF4444' }} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Upgrade CTA */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(var(--sv-accent-rgb), 0.04)', border: '1px solid rgba(var(--sv-accent-rgb), 0.1)' }}>
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold" style={{ color: 'var(--sv-gold-primary)' }}>Limite de colaboradores</p>
                        <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted)' }}>Amplie sua equipe com planos superiores. Gerencie até 100+ colaboradores.</p>
                    </div>
                    <a href="/planos" className="text-[10px] font-medium px-3 py-1.5 rounded-lg transition-all hover:scale-105 shrink-0" style={{ background: 'rgba(var(--sv-accent-rgb), 0.15)', color: 'var(--sv-gold-primary)' }} data-testid="upgrade-colaboradores">
                        Ver planos <span className="inline ml-0.5">&#8594;</span>
                    </a>
                </div>
            </div>
        </div>
    );
}
